/*=== CORE IMPORTS ===*/
import { html } from 'lit';
import { ViewBase } from '../view-base.js';
import { baseProjectViewStyles } from './base-project-view-styles.js';
import { projectProfileViewStyles } from './project-profile-view-styles.js';
import { RecordActionsMixin } from '../../modules/record-actions-mixin.js';
import { FormHostMixin } from '../../modules/form-host-mixin.js';

/*=== LIBRARY COMPONENTS ===*/
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/avatar/avatar.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/divider/divider.js';
import '@shoelace-style/shoelace/dist/components/dialog/dialog.js';
import '@shoelace-style/shoelace/dist/components/dropdown/dropdown.js';
import '@shoelace-style/shoelace/dist/components/radio-group/radio-group.js';
import '@shoelace-style/shoelace/dist/components/radio-button/radio-button.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/menu-item/menu-item.js';
import '@shoelace-style/shoelace/dist/components/menu/menu.js';
import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';
import '@shoelace-style/shoelace/dist/components/tag/tag.js';
import '@shoelace-style/shoelace/dist/components/tooltip/tooltip.js';

/*=== Q1 COMPONENTS ===*/
import '../../components/q1-markdown/q1-markdown.js';
import '../../components/q1-spinner-panel/q1-spinner-panel.js';
import '../../components/q1-version-history/q1-version-history.js';

/*=== Q1 MODULES ===*/
import { buildRefPathMap } from '../../modules/cache-helpers-client.js';
import { enrichProject } from '../../modules/project-helpers-client.js';
import { enrichMember } from '../../modules/member-helpers-client.js';
import { enrichMembership } from '../../modules/membership-helpers-client.js';
import { projectMemberActionsMap_byContext } from '../../models/project-member-client-actions.js';
import { q1DocFunctions, q1VersionFunctions } from '../../modules/q1-core-functions.js';
import {
  projectMemberRoleMap, projectMemberRoleOptions,
} from '../../models/project-member-client-enums.js';

/*=== CONSTANTS ===*/

const PLACE_TREE_CACHE = 'all-places-tree';

const MEMBER_SEARCH_CACHE = 'all-members-search';

const CACHE_MAX_AGE = 60; // in minutes

const PROPOSE_FLOW_KEYS = {
  'deactivate': 'projectMembers.deactivate',
  'invite': 'projectMembers.invite',
  'request-supporter': 'projectMembers.request',
  'request-contributor': 'projectMembers.request',
  'join-supporter': 'projectMembers.request',
  'join-contributor': 'projectMembers.request',
};

/*=== VIEW CLASS ===*/

export class ProjectProfileView extends RecordActionsMixin(FormHostMixin(ViewBase)) {
  static styles = [
    ...super.styles,
    baseProjectViewStyles,
    projectProfileViewStyles,
  ];

  static properties = {
    ...super.properties,

    /**
     * Values:
     * - `init`: Starting state, shows page load skeleton
     * - `not-found`: Displays 404-like page
     * - `not-visible`: Displays error message around permissions
     * - `loaded`: Displays loaded project
     * - `load-error`: Displays error message in notice panel. This is different from `this.error`.
     *   The error boolean can be true in any view mode. Load error is specific to errors which
     *   prevent the original load from succeeding.
     */
    viewMode: { type: String },

    projectKey: { type: String },
    memberKey: { type: String },
    projectMemberKey: { type: String },
    
    projectMember: { type: Object },
    project: { type: Object },
    member: { type: Object },
    
    context: { type: Object },
    refPathMap: { type: Object },

    currentAction: { type: String },
    role: { type: String },
    proposerNote: { type: Array },

    loading: { type: Boolean },
    error: { type: Boolean },
    errorMessage: { type: String },
    processing: { type: Boolean },
  };

  constructor() {
    super();

    // Begin by creating a `this.initComplete` promise that resolves when the view is initialized.
    this.resetInitParams();

    this.viewMode = 'init';

    this.projectKey = null;
    this.memberKey = null;
    this.projectMemberKey = null;

    this.projectMember = null;
    this.project = null;
    this.member = null;
    
    this.context = null;
    this.refPathMap = null;

    this.currentAction = null;
    this.role = null;
    this.proposerNote = '';

    this.loading = true;
    this.error = false;
    this.errorMessage = null;
    this.processing = false;
  }

  /*=== LIFECYCLE ===*/

  connectedCallback() {
    super.connectedCallback();

    this._loadProjectMemberAndSetViewMode();
  }

  updated(changedProperties) {
    super.updated(changedProperties);

    if (changedProperties.has('routeParams')) {
      this._loadProjectMemberAndSetViewMode();
    }
  }

  /** Overwriting base project view method to query for project member instead project. */
  async _loadProjectMemberAndSetViewMode() {
    const newProjectKey = this.routeParams?.key;
    const newMemberKey = this.routeParams?.memberKey;

    if (
      !newProjectKey || !newMemberKey || (
        (this.projectKey === newProjectKey)
        && (this.memberKey === newMemberKey)
      )
    ) return;

    this.projectKey = newProjectKey;
    this.memberKey = newMemberKey;
    this.projectMemberKey = `${this.projectKey}:${this.memberKey}`;

    return this._refreshProjectMember();
  }

  /**
   * Overwrite this method to kick off a process right alongside the project member load.
   * 
   * This method is SYNCHRONOUS and will not hold up the project memberload.
   */
  projectMemberLoadStarted() {}

  /**
   * Overwrite this method to do something after project member is loaded, but before the `loading`
   * boolean is set to false. Throw an error to set the view mode to `load-error`.
   * Or just overwrite the view mode to do something else.
   * 
   * This method is ASYNCHRONOUS and WILL hold up the view initialization.
   */
  async projectMemberLoaded() {}

  /** Call this to set the css styles from source object */
  _setProjectStyles(sourceObject = this.project, targetElement = this) {
    if (sourceObject) {
      targetElement.style.setProperty('--theme-color-primary', sourceObject.colorPrimary);
      targetElement.style.setProperty('--theme-color-secondary', sourceObject.colorSecondary);
      targetElement.style.setProperty('--project-overlay-opacity', sourceObject.overlayOpacity);
      targetElement.style.setProperty('--project-banner-opacity', sourceObject.bannerOpacity);
      targetElement.style.setProperty('--theme-text-color-primary', sourceObject.textColorPrimary_hex);
      targetElement.style.setProperty('--theme-text-color-secondary', sourceObject.textColorSecondary_hex);
    } else {
      targetElement.style.setProperty('--theme-color-primary', 'var(--teal-7)');
      targetElement.style.setProperty('--theme-color-secondary', 'var(--teal-7)');
      targetElement.style.setProperty('--project-overlay-opacity', '0.5');
      targetElement.style.setProperty('--project-banner-opacity', '0.5');
      targetElement.style.setProperty('--theme-text-color-primary', 'white');
      targetElement.style.setProperty('--theme-text-color-secondary', 'white');
    }
  }

  async _refreshProjectMember() {
    this.loading = true;
    this.error = false;
    this.errorMessage = '';
    
    const { getDoc } = q1DocFunctions;
    
    try {
      this.projectMemberLoadStarted(); // Allow for custom logic to kick off in parallel
      const response = await getDoc({
        docCollection: 'projectMembers',
        docKey: this.projectMemberKey,
      });
      const responseData = response.data;
      const { docData: projectMember, currentMemberContext, project, member } = responseData;
      
      // Get the places cache to enrich location data
      let placesCache =
        await this.appElement.cacheManager.getCache(PLACE_TREE_CACHE, CACHE_MAX_AGE);
      if (placesCache.isNew) placesCache = await placesCache.refreshComplete; // it's ok if stale
      this.refPathMap = buildRefPathMap(placesCache.data, 'places', 'places');

      // Now enrich the member, project, etc
      this.member = enrichMember(member, this.refPathMap);
      this.refPathMap[this.member.ref] = this.member;
      this.project = enrichProject(project);
      this.refPathMap[this.project.ref] = this.project;
      this._setProjectStyles();

      // Finally enrich the project member (it needs the project)
      this.projectMember = enrichMembership(projectMember, this.refPathMap);
      this.context = currentMemberContext;
      this.setAfLink_db('projectMembers', this.projectMemberId);

      // If version history is visible start member cache query in parallel
      if (this.canSeeHistory) this.queryMemberCache();

      // Call the projectMemberLoaded method to allow child pages to set title & insert custom logic
      await this.projectMemberLoaded();
      this.setViewMeta(this.loadedDocumentTitle);
      this.viewMode = 'loaded';
      this._resolveInit(); // resolves the `this.initComplete` promise
    } catch (error) {
      console.error('Error Loading Project Profile:', error);

      switch (error.details?.resultCode) {
        case 'not-found':
          this.setViewMeta('Project Profile Not Found - q1 Network');
          this.viewMode = 'not-found';
          break;

        case 'view-not-allowed':
          this.setViewMeta('Project Profile Not Visible - q1 Network');
          this.viewMode = 'not-visible';
          break;

        default:
          this.setViewMeta('Project Profile Error - q1 Network');
          this.error = true;
          this.errorMessage = error.message;
          this.viewMode = 'load-error';
          break;
      }
    } finally {
      this.loading = false;
    }
  }

  async queryMemberCache() {
    let memberSearchCache =
        await this.appElement.cacheManager.getCache(MEMBER_SEARCH_CACHE, CACHE_MAX_AGE);
    
    this.refPathMap = {
      ...this.refPathMap,
      ...buildRefPathMap(memberSearchCache.data, 'members', 'members')
    };
    
    if (memberSearchCache.isNewOrStale) {
      memberSearchCache = await memberSearchCache.refreshComplete;
      
      this.refPathMap = {
        ...this.refPathMap,
        ...buildRefPathMap(memberSearchCache.data, 'members', 'members')
      };
    }
  }

  /*=== ACTIONS ===*/

  showProposeDialog() {
    if (!this.currentAction || !this._allowedActions.includes(this.currentAction)) {
      this.showToast('This action is not allowed', null, 'danger');
      return;
    }

    this.renderRoot.getElementById('propose-dialog').show();
  }

  closeProposeDialog() {
    this.renderRoot.getElementById('propose-dialog').hide();
  }

  async propose() {
    const proposeFlowKey = PROPOSE_FLOW_KEYS[this.currentAction];
    if (!proposeFlowKey || !this._allowedActions.includes(this.currentAction)) {
      this.showToast('This action is not allowed', null, 'danger');
      return;
    }

    const proposeForm = this.renderRoot.getElementById('propose-form');
    if (!proposeForm.reportValidity()) {
      this.showToast('Please fill in the required fields to continue', null, 'danger');
      return;
    }

    const { proposeNewVersion } = q1VersionFunctions;
    this.processing = true;

    try {
      const requestData = {
        flowKey: proposeFlowKey,
        relatedDocIds: {
          projectId: this.project.id,
          memberId: this.member.id,
        },
        proposerNote: this.proposerNote,
      };
      if (this.currentAction !== 'deactivate') requestData.opParams = {
        role: this.role || this.roleDefaultValue, // this.role is blank if no change event fired
      };
      
      const response = await proposeNewVersion(requestData);

      this.showToast(this.proposeSuccessMessage, null, 'success', 5000);

      // Wait 2 seconds then reload the project member (and version history if needed)
      await new Promise(resolve => setTimeout(resolve, 2000));
      this._refreshProjectMember();
      if (this.canSeeHistory) this.renderRoot.getElementById('version-history')?.loadHistory();

      this.closeProposeDialog();
    } catch (error) {
      console.error('Error proposing new version of project membership:', error);
      this.showToast(error.message, 'There was an error submitting your action!', 'danger');
    } finally {
      this.processing = false;
    }
  }

  /*=== RECORD ACTIONS IMPLEMENTATION METHODS ===*/

  get _recordActionsMap() {
    return projectMemberActionsMap_byContext(this.context);
  }

  get _allowedActions() {
    return this.context?.allowedActions || [];
  }

  get _recordActionButtons_processing() {
    return this.processing;
  }

  handleAction(actionKey) {
    switch (actionKey) {
      case 'edit':
        this.navigateTo(this.editProfilePath);
        break;

      case 'deactivate':
      case 'invite':
      case 'request-supporter':
      case 'request-contributor':
      case 'join-supporter':
      case 'join-contributor':
        this.currentAction = actionKey;
        this.showProposeDialog();
        break;

      default:
        console.error(`UNHANDLED action key: ${actionKey}`);
    }
  }

  /*=== HANDLERS ===*/

  // None yet

  /*=== GETTERS ===*/

  /** Overwrite this to change the doc title for a successfully loaded record. */
  get loadedDocumentTitle() {
    return `${this.member.name} - ${this.project.name} - q1 Network`;
  }

  /** Overwriting this from view-base to conditionally set white text on q1-app-header. */
  get appHeaderVariant() {
    // NOTE: The very top of the view content is the fully saturated primary color, so this setting
    // tells us whether we need white text to contrast with that.
    if (!this.project) return 'white-text';
    return this.project?.textColorPrimary_isWhite ? 'white-text' : 'default';
  }

  get projectMemberId() {
    return this.projectMember?.id;
  }

  get projectPath() {
    if (!this.projectKey) return '';

    return `projects/${this.projectKey}`;
  }
  
  get editProfilePath() {
    if (!this.projectMember?.key?.includes?.(':')) return '';

    const [projectKey, memberKey] = this.projectMember.key.split(':');
    return `projects/${projectKey}/@${memberKey}/edit`;
  }

  get canSeeHistory() {
    if (!this.isSignedIn) return false;
    if (this.context?.isSelf) return true;
    return this.context?.project?.visibleData?.includes?.('member-profile-history') || false;
  }

  get proposedVersionPath() {
    if (!this.projectMemberId || !this.projectMember?.versionNumberProposed) return '';

    return `/v/projectMembers:${this.projectMemberId}:v${this.projectMember.versionNumberProposed}`;
  }

  get hasProposedVersion() {
    const { versionNumberProposed, versionNumber } = this.projectMember || {};
    
    return versionNumberProposed && versionNumber && versionNumberProposed > versionNumber;
  }

  get metaContext() {
    return { project: this.project, projectMember: this.projectMember };
  }  
  
  /*=== PROPOSE ACTION GETTERS ===*/

  get proposeDialogExplainerText() {
    const fillFormTo = 'Fill in the form below to ';

    switch (this.currentAction) {
      case 'deactivate':
        return this.context?.isSelf
          ? `${fillFormTo} deactivate your project membership.`
          : `${fillFormTo} deactivate this member.`;

      case 'invite':
        return this.projectMember.status === 'active'
          ? `${fillFormTo} invite this member to a new role within the project.`
          : `${fillFormTo} invite this member to re-activate their project membership.`;
      
      case 'request-supporter':
      case 'request-contributor':
        return `${fillFormTo} request a different project role.`;
      
      case 'join-supporter':
      case 'join-contributor':
        return `${fillFormTo} change your project role.`;
      
      default:
        return 'INVALID ACTION';
    }
  }

  get showRole() {
    switch (this.currentAction) {
      case 'deactivate':
        return false;

      case 'invite':
      case 'request-supporter':
      case 'request-contributor':
      case 'join-supporter':
      case 'join-contributor':
        return true;
      
      default:
        return false;
    }
  }

  get roleOptions() {
    switch (this.currentAction) {
      case 'deactivate':
        return [];

      case 'invite':
        return projectMemberRoleOptions
          .filter(o => o.invitableBy.includes(this.context.project.role))
          .map(o => ({
            ...o,
            disabled: this.projectMember.status === 'active' && o.value === this.projectMember.role,
          }));

      case 'request-supporter':
      case 'join-supporter':
        return [projectMemberRoleMap.supporter];

      case 'request-contributor':
      case 'join-contributor':
        return [projectMemberRoleMap.contributor];
      
      default:
        return [];
    }
  }

  get roleDefaultValue() {
    const roleOptions = this.roleOptions;
    
    if (!roleOptions.length) return '';

    // Else return the first non-disabled option
    return roleOptions.filter(o => !o.disabled)[0]?.value || '';
  }

  get proposeDialogTitle() {
    switch (this.currentAction) {
      case 'deactivate':
        return 'Project Membership Deactivation';

      case 'invite':
        return 'Project Membership Invitation';
      
      case 'request-supporter':
      case 'request-contributor':
        return 'Project Membership Request';
      
      case 'join-supporter':
      case 'join-contributor':
        return 'Project Membership Change';
      
      default:
        return 'INVALID ACTION';
    }
  }
  
  get proposerNoteLabel() {
    switch (this.currentAction) {
      case 'deactivate':
        return 'Deactivation Reason';

      case 'invite':
        return 'Invitation Message';
      
      case 'request-supporter':
      case 'request-contributor':
        return 'Request Message';
      
      case 'join-supporter':
      case 'join-contributor':
        return 'Role Change Reason';
      
      default:
        return 'INVALID ACTION';
    }
  }
  
  get proposerNoteHelpText() {
    const endText =
      ' This is included as part of the membership history and is visible to all project members.';
    
      switch (this.currentAction) {
      case 'deactivate':
        return (
          this.context.isSelf
            ? "Please provide a brief explanation of why you're leaving the project."
            : "Please provide a brief explanation of why this member is being deactivated."
        ) + endText;

      case 'invite':
        return 'Invitation message to the member.' + endText;
      
      case 'request-supporter':
      case 'request-contributor':
        return 'Request message to the project organizers.' + endText;
      
      case 'join-supporter':
      case 'join-contributor':
        return "Please provide a brief explanation of why you're changing roles." + endText;
      
      default:
        return 'INVALID ACTION';
    }
  }

  get proposeButtonLabel() {
    switch (this.currentAction) {
      case 'deactivate':
        return this.context.isSelf
          ? 'Deactivate My Membership'
          : 'Deactivate Member';

      case 'invite':
        return 'Send Invitation';
      
      case 'request-supporter':
      case 'request-contributor':
        return 'Submit Request';
      
      case 'join-supporter':
      case 'join-contributor':
        return 'Change Project Role';
      
      default:
        return 'INVALID ACTION';
    }
  }

  get proposeSuccessMessage() {
    switch (this.currentAction) {
      case 'deactivate':
        return 'Project membership deactivation successful!';

      case 'invite':
        return 'Invitation successfully sent!';
      
      case 'request-supporter':
      case 'request-contributor':
        return 'Request successfully submitted!';
      
      case 'join-supporter':
      case 'join-contributor':
        return 'Project role successfully changed!';
      
      default:
        return 'INVALID ACTION';
    }
  }

  /*=== TEMPLATES ===*/

  get contentTemplate() {
    return html`
      ${this.contentHeaderTemplate}

      ${this.viewModeTemplate}

      <q1-spinner-panel label="Loading..." ?open=${this.loading}></q1-spinner-panel>
    `;
  }

  get contentHeaderTemplate() {
    if (['init'].includes(this.viewMode)) return null;

    return html`
      <header-bar class="small" id="profile-header-bar">
        ${this.project && this.projectMember ? html`

          <a
            href=${this.project.detailViewPath}
            class="avatar-link"
            alt=${this.project.name}
          >
            <sl-avatar
              image=${this.project.avatar?.small?.url}
              label=${this.project.name}
              shape=${this.project.avatar_shape}
            >
              <sl-icon slot="icon" library="material" name=${this.project.avatar_icon}></sl-icon>
            </sl-avatar>
          </a>
          
          <h1>
            <a class="prefix" href=${this.project.detailViewPath}>${this.project.name}</a>
            Project Member Profile
          </h1>

          ${!this.hasRecordActionButtons ? '' : html`
            <button-bar class="sl-themed-secondary">
              ${this.recordActionButtonsTemplate}
            </button-bar>
          `}
        ` : html`

            <h1>
              <span class="prefix">Project Member Profile</span>
              Project Member Key: ${this.projectMemberKey}
            </h1>

        `}
      </header-bar>
    `;
  }

  get viewModeTemplate() {
    switch (this.viewMode) {
      case 'init':
        return this.viewModeTemplate_init;

      case 'not-found':
        return this.viewModeTemplate_notFound;

      case 'not-visible':
        return this.viewModeTemplate_notVisible;

      case 'load-error':
        return this.viewModeTemplate_loadError;
        
      case 'loaded':
        return this.viewModeTemplate_loaded;

      default:
        this.errorMessage = `Unknown view mode: ${this.viewMode}`;
        this.error = true;
        return this.viewModeTemplate_loadError;
    }
  }

  get viewModeTemplate_init() {
    // IMPROVEME: Add skeleton loading view
    return null;
  }

  get viewModeTemplate_notFound() {
    return html`
      <notice-panel class="hero">
        <div class="inner">
          <sl-icon library="material" name="block"></sl-icon>

          <h2>Project profile not found...</h2>

          <p>
            Please check the URL and try again.
          </p>
        </div>
      </notice-panel>
    `;
  }

  get viewModeTemplate_notVisible() {
    return html`
      <notice-panel class="hero secondary">
        <div class="inner">
          <sl-icon library="material" name=${this.notVisibleIcon}></sl-icon>

          <h2>${this.notVisibleTitle}</h2>

          ${this.notVisibleMessage}
      </notice-panel>
    `;
  }

  get notVisibleIcon() {
    if (!this.isSignedIn) return 'public_off';
    return 'lock';
  }

  get notVisibleTitle() {
    if (!this.isSignedIn) return 'This project profile is not publicly visible';
    return 'This is a private project profile';
  }

  get notVisibleMessage() {
    if (!this.isSignedIn) return html`
      <p>
        If you want to explore this project,
        the first step is joining the q1 Network!
      </p>

      <sl-button
        variant="warning"
        size="large"
        href="/register"
        pill
      >Register Now</sl-button>

      <p class="footer">
        Already a member?
        <a href="/signin">Sign in here</a>.
        <br/>
        Curious about the q1 Network?
        <a href="/join">Learn more about how it works</a>.
      </p>
    `;

    return html`
      <p>
        If you're interested in this project, try reaching out to the organizers in Slack.
      </p>

      <p>
        Check the <a href=${this.projectPath}>project page</a> for more info.
      </p>
    `;
  }

  get viewModeTemplate_loadError() {
    return html`
      <notice-panel class="hero danger">
        <div class="inner">
          <sl-icon library="material" name="error"></sl-icon>

          <h2>An error occurred!</h2>

          <p>
            ${this.errorMessage}
          </p>
        </div>
      </notice-panel>
    `;
  }
  
  get viewModeTemplate_loaded() {
    return html`
      ${!this.error ? '' : html`
        <sl-alert variant="danger" open>
          <sl-icon slot="icon" name="exclamation-triangle"></sl-icon>
          ${this.errorMessage}
        </sl-alert>
      `}

      <project-profile-content class="sl-themed-secondary">
        <header-bar id="title-header-bar" class="nested">
          <a
            href=${this.member.profilePath}
            class="avatar-link"
            alt=${this.member.name}
          >
            <sl-avatar
              image=${this.member.profile?.small?.url}
              label=${this.member.name}
              shape="circle"
            ></sl-avatar>
          </a>
          
          <div id="member-title">
            <a href=${this.member.profilePath} class="title-link">
              <h1>
                ${this.member.name}
              </h1>
            </a>
            <div id="member-details">
              <span id="memberNumber">@${this.member.number}</span>

              ${!this.member.locationsPrimary_full?.length ? '' : html`
                <span class="separator">—</span>
                
                ${this.member.locationsPrimary_full.map(location => html`
                  <span class="location">
                    <sl-icon library="material" name="home_pin"></sl-icon>
                    ${location.name}
                  </span>
                `)}
              `}
            </div>
          </div>

          ${this.projectMember.status === 'active' ? null : html`
            <sl-tooltip content=${this.projectMember.status_description}>
              <sl-tag
                size="large"
                variant="primary"
                class="project-member-status ${this.projectMember.status}"
                pill
              >
                <sl-icon
                  library=${this.projectMember.status_iconLibrary}
                  name=${this.projectMember.status_icon}
                ></sl-icon>
                ${this.projectMember.status_label}
              </sl-tag>
            </sl-tooltip>
          `}
        </header-bar>
        
        <div id="details-wrapper">
          <ul id="details-panel">
            <li>
              <sl-icon
                library=${this.projectMember.role_iconLibrary}
                name=${this.projectMember.role_icon}
              ></sl-icon>
              <span>
                ${this.projectMember.statusRoleLabel}
              </span>
            </li>

            <li>
              <sl-tooltip>
                <div slot="content">
                  ${this.projectMember.status_timestampLabel}
                  <sl-relative-time .date=${this.projectMember.status_timestamp}></sl-relative-time>
                </div>

                <sl-icon library="material" name="calendar_month"></sl-icon>
                <span>
                  ${this.projectMember.status_timestampLabel}
                  <sl-format-date
                    date=${this.projectMember.status_timestamp}
                    month="short" day="numeric" year="numeric"
                  ></sl-format-date>
                </span>
              </sl-tooltip>
            </li>

            <li>
              <sl-tooltip content=${this.projectMember.visibility_description}>
                <sl-icon
                  library=${this.projectMember.visibility_iconLibrary}
                  name=${this.projectMember.visibility_icon}
                ></sl-icon>
                <span>${this.projectMember.visibility_label}</span>
              </sl-tooltip>
            </li>

            ${!this.projectMember.profileUpdateCount ? null : html`
              <li>
                <sl-tooltip>
                  <div slot="content">
                    Last edited
                    <sl-format-date
                      date=${this.projectMember.profileUpdatedAt}
                      month="numeric" day="numeric" year="numeric"
                    ></sl-format-date>
                  </div>

                  <sl-icon library="material" name="update"></sl-icon>
                  <span>${this.projectMember.profileUpdateCount_text}</span>
                </sl-tooltip>
              </li>
            `}
          </ul>
          ${!this.hasRecordActionButtons && !this.hasRecordActionMenuItems ? '' : html`
            <div id="details-actions">
              ${this.recordActionMenuTemplate}
            </div>
          `}
        </div>

        ${this.activeProposedVersionPanel}
        
        ${this.profileContentTemplate}
      </project-profile-content>

      ${this.versionHistoryTemplate}

      ${this.proposeDialogTemplate}
    `;
  }

  /** This panel only displays when status is active. Proposed shows in profileContentTemplate. */
  get activeProposedVersionPanel() {
    if (this.projectMember?.status !== 'active' || !this.hasProposedVersion) return null;
    if (!this.canSeeHistory) return null;

    return html`
      <proposed-version-panel>
        <sl-icon library="material" name="pending"></sl-icon>
        <div class="title">Proposed Change Pending:</div>
        <div class="description">
          ${this.context?.isSelf ? 'Your project membership' : 'This project member'}
          has a proposed change pending approval.
        </div>
        <sl-button
          size="small"
          variant="primary"
          href=${this.proposedVersionPath}
          pill
        >
          <sl-icon slot="prefix" library="material" name="arrow_forward"></sl-icon>
          View Proposed Version
        </sl-button>
      </proposed-version-panel>
    `;
  }

  get profileContentTemplate() {
    if (this.projectMember?.status === 'proposed') return html`
      <p class="subtitle hero">
        This is a proposed project membership that has yet to be accepted.
        ${!this.canSeeHistory ? '' : html`
          Click the link below to track the approval process.
        `}
      </p>
      
      ${!this.canSeeHistory ? '' : html`
        <sl-button
          size="large"
          href=${this.proposedVersionPath}
          pill
        >
          <sl-icon slot="prefix" library="material" name="arrow_forward"></sl-icon>
          View Proposed Version
        </sl-button>
      `}
    `;
    
    if (!this.projectMember.headline || !this.projectMember.profile) return html`
      <p class="subtitle hero">
        This project membership does not yet have any profile content.
      </p>
    `;

    return html`
      <h2 class="main-header">${this.projectMember.headline}</h2>

      <q1-markdown .content=${this.projectMember.profile}></q1-markdown>
    `;
  }

  get versionHistoryTemplate() {
    if (!this.canSeeHistory) return null;

    return html`
      <q1-version-history
        id="version-history"
        title="Project Membership History"
        subject=${this.projectMember.ref}
        .refPathMap=${this.refPathMap}
      ></q1-version-history>
    `;
  }

  get proposeDialogTemplate() {
    if (!this._allowedActions?.length) return null;

    return html`
      <sl-dialog
        id="propose-dialog"
        label=${this.proposeDialogTitle}
      >
        <p class="subtitle hero primary sl-themed">
          ${this.proposeDialogExplainerText}
        </p>

        <form id="propose-form">
          
          ${!this.showRole ? '' : html`
            <sl-radio-group
              id="role"
              name="role"
              label="New Project Role"
              value=${this.roleDefaultValue}
              ?disabled=${this.processing}
              @sl-change=${this.handleChange}
              required
            >
              ${this.roleOptions.map(({ value, icon, label, iconLibrary, disabled }) => html`
                <sl-radio-button value=${value} ?disabled=${disabled}>
                  <sl-icon slot="prefix" library=${iconLibrary} name=${icon}></sl-icon>
                  ${label}
                </sl-radio-button>
              `)}
            </sl-radio-group>
          `}
          
          <sl-textarea
            id="proposerNote"
            name="proposerNote"
            label=${this.proposerNoteLabel}
            help-text=${this.proposerNoteHelpText}
            maxlength="500"
            rows="5"
            ?disabled=${this.processing}
            @sl-input=${this.handleInput}
            required
            autofocus
          ></sl-textarea>
        </form>

        <q1-spinner-panel
          label="Processing..."
          ?open=${this.processing}
        ></q1-spinner-panel>

        <button-bar slot="footer">
          <sl-button
            @click="${this.closeProposeDialog}"
          >Cancel</sl-button>

          <sl-button
            variant=${this.currentAction === 'deactivate' ? 'danger' : 'primary'}
            @click=${this.propose}
          >
            ${this.proposeButtonLabel}
          </sl-button>
        </button-bar>
      </sl-dialog>
    `;
  }

}

customElements.define('project-profile-view', ProjectProfileView);
